import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";

const ProductDetailsModal = ({
  open,
  onClose,
  product,
  onConfirm,
  IMAGE_BASE_URL,
  placeholderImage,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");

  const handleClose = () => {
    setQuantity(1);
    setNote("");
    onClose();
  };

  const handleConfirm = () => {
    onConfirm({
      ...product,
      quantity: quantity,
      note: note,
    });
    handleClose();
  };

  const handleIncrement = () => {
    setQuantity(prev => prev + 1);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 },
          bgcolor: "#fff",
          boxShadow: "0 8px 32px rgba(255, 61, 61, 0.15)",
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: "100%", sm: "90vh" },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "#fffbe6",
          borderBottom: "2px solid #ffe0b2",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: "#ff3d3d",
            fontSize: { xs: 18, sm: 20, md: 22 },
          }}
        >
          تفاصيل المنتج
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#ff3d3d",
            "&:hover": { bgcolor: "#fff3f3" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          bgcolor: "#fff",
        }}
      >
        {/* Product Image */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: { xs: 150, sm: 200 },
              height: { xs: 150, sm: 200 },
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid #fffbe6",
              boxShadow: "0 4px 16px rgba(255, 179, 0, 0.2)",
            }}
          >
            <img
              src={
                product.image
                  ? `${IMAGE_BASE_URL}${product.image}`
                  : placeholderImage
              }
              alt={product.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Box>

        {/* Product Info */}
        <Typography
          sx={{
            fontWeight: 800,
            color: "#222",
            fontSize: { xs: 18, sm: 20, md: 22 },
            mb: 1,
            textAlign: "center",
          }}
        >
          {product.name}
        </Typography>

        <Typography
          sx={{
            color: "#888",
            fontSize: { xs: 13, sm: 14, md: 15 },
            mb: 2,
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          {product.description}
        </Typography>

        <Typography
          sx={{
            color: "#ff3d3d",
            fontWeight: 900,
            fontSize: { xs: 20, sm: 22, md: 24 },
            mb: 3,
            textAlign: "center",
          }}
        >
          {product.price} ج.م
        </Typography>

        {/* Quantity Selector */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#222",
              fontSize: { xs: 14, sm: 15 },
              mb: 1.5,
            }}
          >
            الكمية
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <IconButton
              onClick={handleDecrement}
              disabled={quantity <= 1}
              sx={{
                bgcolor: "#fffbe6",
                border: "2px solid #ffe0b2",
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                "&:hover": {
                  bgcolor: "#ffb300",
                  borderColor: "#ffb300",
                  "& .MuiSvgIcon-root": { color: "#fff" },
                },
                "&.Mui-disabled": {
                  bgcolor: "#f5f5f5",
                  borderColor: "#e0e0e0",
                },
              }}
            >
              <RemoveIcon sx={{ color: "#ff3d3d", fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 24, sm: 28 },
                color: "#222",
                minWidth: { xs: 50, sm: 60 },
                textAlign: "center",
              }}
            >
              {quantity}
            </Typography>

            <IconButton
              onClick={handleIncrement}
              sx={{
                bgcolor: "#fffbe6",
                border: "2px solid #ffe0b2",
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                "&:hover": {
                  bgcolor: "#ffb300",
                  borderColor: "#ffb300",
                  "& .MuiSvgIcon-root": { color: "#fff" },
                },
              }}
            >
              <AddIcon sx={{ color: "#ff3d3d", fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>
          </Box>
        </Box>

        {/* Note Field */}
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#222",
              fontSize: { xs: 14, sm: 15 },
              mb: 1.5,
            }}
          >
            ملاحظات (اختياري)
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="أضف ملاحظاتك هنا..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{
              bgcolor: "#fffef5",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ffe0b2",
                  borderWidth: 2,
                },
                "&:hover fieldset": {
                  borderColor: "#ffb300",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff3d3d",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: { xs: 13, sm: 14 },
                fontFamily: "Cairo, Arial, sans-serif",
                color: "#000",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#888",
                opacity: 1,
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          bgcolor: "#fffbe6",
          borderTop: "2px solid #ffe0b2",
          gap: { xs: 1, sm: 1.5 },
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Button
          onClick={handleClose}
          fullWidth={isMobile}
          sx={{
            fontWeight: 700,
            fontSize: { xs: 14, sm: 15 },
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: 2,
            bgcolor: "#fff",
            color: "#888",
            border: "2px solid #e0e0e0",
            "&:hover": {
              bgcolor: "#f5f5f5",
              borderColor: "#d0d0d0",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          fullWidth={isMobile}
          sx={{
            fontWeight: 800,
            fontSize: { xs: 14, sm: 15 },
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: 2,
            bgcolor: "#ffb300",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(255, 179, 0, 0.3)",
            "&:hover": {
              bgcolor: "#ff3d3d",
              boxShadow: "0 4px 12px rgba(255, 61, 61, 0.4)",
            },
          }}
        >
          إضافة للسلة ({quantity} × {(product.price * quantity).toFixed(2)} ج.م)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailsModal;
